import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {ExpandableContent} from './ExpandableContent'
import {useOverviewRouteData} from './data/use-overview-route-data'
import styles from './CopilotReviewSummary.module.css'

/**
 * Renders the latest Copilot Code Review summary below the PR description.
 * Returns null when no Copilot review exists or the feature flag is disabled.
 */
export function CopilotReviewSummary() {
  const {copilotReviewSummary} = useOverviewRouteData()

  if (!isFeatureEnabled('prs_overview_copilot_summary')) return null
  if (!copilotReviewSummary) return null

  const body = copilotReviewSummary.body
  const changesHTML = extractChangesHTML(body)

  return (
    <section className={styles.container} aria-label="Copilot code review summary">
      <h3 className={styles.title}>Copilot-generated summary ✨</h3>
      <ExpandableContent
        sectionLabel="Copilot review summary"
        storageKey="overview-copilot-summary-expanded"
        defaultExpanded
      >
        {/* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- changesHTML is derived from copilotReviewSummary.body, which is already a SafeHTMLString sanitized server-side. */}
        <NewMarkdownViewer verifiedHTML={(changesHTML ?? body) as SafeHTMLString} />
      </ExpandableContent>
    </section>
  )
}

/**
 * Extracts just the "Changes" bullet list (<ul>) from a Copilot review body.
 * Returns null if the expected structure isn't found, so callers can fall back
 * to rendering the full body.
 */
export function extractChangesHTML(body: string): string | null {
  if (!body) return null

  const labelMatch = body.match(/<strong>\s*Changes\s*:?\s*<\/strong>/i)
  if (!labelMatch || labelMatch.index === undefined) return null

  const afterLabel = body.slice(labelMatch.index)
  const ulMatch = afterLabel.match(/<ul\b[^>]*>[\s\S]*?<\/ul>/i)
  if (!ulMatch) return null

  return `${ulMatch[0]}`
}
